<template>
	<div class="line">
		<div class="dialog-tit">
			<span class="border"></span>
			<span>{{ config.sectionLabel }}</span>
		</div>
		<div class="food-con">
			<el-form-item
				v-for="tf in (config.textFields || [])"
				:key="tf.frontKey"
				:label="tf.label"
				:prop="tf.frontKey"
			>
				<el-input
					type="textarea"
					:autosize="{ minRows: 2, maxRows: 4 }"
					class="longInput"
					:model-value="form[tf.frontKey]"
					@update:model-value="onTextFieldUpdate(tf.frontKey, $event)"
				></el-input>
			</el-form-item>

			<!-- 商城商品分支：选品弹窗 + ProductTagItem -->
			<template v-if="config.selectorKind === 'mallProduct'">
				<el-form-item :label="config.innerLabel">
					<div class="product-action">
						<el-button size="small" type="primary" plain @click="openProductDialog">
							<el-icon style="margin-right: 4px;"><Plus /></el-icon>
							选择商品
						</el-button>
						<span class="product-count" v-if="selectedProducts.length">
							已选 {{ selectedProducts.length }} 个
						</span>
					</div>
					<div class="product-list" v-if="selectedProducts.length > 0">
						<ProductTagItem
							v-for="p in selectedProducts"
							:key="p.id"
							:product="p"
							@remove="onRemoveProduct(p)"
						/>
					</div>
				</el-form-item>
				<ProductSelectorDialog
					v-model="dialogVisible"
					:selected-ids="selectedIds"
					@confirm="onProductDialogConfirm"
				/>
			</template>

			<!-- 其余推荐分支：autocomplete + tag list（HP2 既有） -->
			<template v-else>
				<div class="food-con-con" v-for="(group, index) in groups" :key="index">
					<el-form-item :label="config.innerLabel">
						<el-autocomplete
							style="width: calc(100% - 60px);"
							popper-class="my-autocomplete"
							:model-value="group[config.itemNameField]"
							@update:model-value="onItemNameInput(index, $event)"
							:fetch-suggestions="fetchSuggestions"
							placeholder="请输入内容"
							@select="(it) => $emit('select', { configKey: config.frontKey, item: it, index })"
						>
							<template #prefix>
								<el-icon class="el-input__icon"><Search /></el-icon>
							</template>
							<template #default="{ item }">
								<div class="img"><span>{{ item.name }}</span></div>
							</template>
						</el-autocomplete>
						<i class="dosageIcon el-icon-circle-plus" @click="addRow"></i>
						<i
							class="dosageIcon el-icon-remove"
							:style="groups.length === 1 ? 'color:#ccc;' : ''"
							@click="deleteRow(index)"
						></i>
						<div
							class="list"
							:class="config.listExtraClass"
							v-if="group.list && group.list.length > 0"
						>
							<ul>
								<li v-for="(it, ii) in group.list" :key="ii">
									<span>{{ it.name }}</span>
									<i
										class="el-icon-delete"
										@click="$emit('delete', { configKey: config.frontKey, tag: it, index })"
									>
										<Delete style="width: 14px;" />
									</i>
								</li>
							</ul>
						</div>
					</el-form-item>
				</div>
			</template>
		</div>
	</div>
</template>

<script>
import { Search, Delete, Plus } from '@element-plus/icons-vue';
import ProductSelectorDialog from './ProductSelectorDialog.vue';
import ProductTagItem from './ProductTagItem.vue';

export default {
	name: 'RecommendGroup',
	components: { Search, Delete, Plus, ProductSelectorDialog, ProductTagItem },
	props: {
		// 当前分组列表（addUserForm[cfg.frontKey]），形如 [{acupointName:'', list:[]}]
		// mallProduct 场景下退化为单个 group，groups[0].list 即已选商品数组
		groups: { type: Array, required: true },
		// 候选数据源（autocomplete 用，mallProduct 不用）
		sourceData: { type: Array, default: () => [] },
		// 整张 addUserForm（用于读写 textFields 文本字段）
		form: { type: Object, required: true },
		// 当前分组配置
		config: { type: Object, required: true },
	},
	emits: ['update:groups', 'select', 'delete', 'updateText'],
	data() {
		return {
			dialogVisible: false,
		};
	},
	computed: {
		// mallProduct 模式：把 groups 里所有 list 扁平化成一个商品数组
		// （后端按多 group 存储，UI 上展示为一个列表）
		selectedProducts() {
			if (this.config.selectorKind !== 'mallProduct') return [];
			const merged = [];
			const seen = new Set();
			(this.groups || []).forEach((g) => {
				(g.list || []).forEach((p) => {
					if (p && p.id != null && !seen.has(p.id)) {
						seen.add(p.id);
						merged.push(p);
					}
				});
			});
			return merged;
		},
		selectedIds() {
			return this.selectedProducts.map((p) => p.id);
		},
	},
	methods: {
		fetchSuggestions(queryString, cb) {
			const list = this.sourceData || [];
			const q = (queryString || '').toLowerCase();
			const results = q
				? list.filter((it) => (it.name || '').toLowerCase().indexOf(q) === 0)
				: list;
			cb(results);
		},
		onItemNameInput(index, value) {
			const next = this.groups.slice();
			next[index] = { ...next[index], [this.config.itemNameField]: value };
			this.$emit('update:groups', next);
		},
		onTextFieldUpdate(frontKey, value) {
			this.$emit('updateText', { frontKey, value });
		},
		addRow() {
			const next = this.groups.slice();
			next.push({ [this.config.itemNameField]: '', list: [] });
			this.$emit('update:groups', next);
		},
		deleteRow(index) {
			if (this.groups.length <= 1) return;
			const next = this.groups.slice();
			next.splice(index, 1);
			this.$emit('update:groups', next);
		},
		// ---- mallProduct 专用 ----
		openProductDialog() {
			this.dialogVisible = true;
		},
		onProductDialogConfirm(products) {
			// 选品 dialog 返回最终全集；扁平化写回，统一塞进 groups[0]
			const next = [{
				[this.config.itemNameField]: '',
				list: products.slice(),
			}];
			this.$emit('update:groups', next);
		},
		onRemoveProduct(p) {
			const remaining = this.selectedProducts.filter((x) => x.id !== p.id);
			const next = [{
				[this.config.itemNameField]: '',
				list: remaining,
			}];
			this.$emit('update:groups', next);
		},
	},
};
</script>

<style lang="less" scoped>
.line {
	width: 100%;
	overflow: hidden;
	margin-bottom: 30px;

	.dosageIcon {
		font-size: 16px;
		color: #666;
		margin-left: 10px;
		cursor: pointer;
	}

	.dialog-tit {
		height: 40px;
		line-height: 40px;

		span {
			float: left;
			font-size: 16px;
			margin-bottom: 20px;
			overflow: hidden;
			width: calc(100% - 24px);
			color: #333;
			margin-left: 10px;
		}

		.border {
			background-color: rgba(103, 159, 246, 1);
			width: 3px;
			height: 14px;
			margin-top: 13px;
			left: 0px;
		}
	}

	.food-con {
		width: 96%;
		margin-left: 2%;
		padding-bottom: 20px;
		float: left;

		.food-con-con {
			margin-left: 0px;
			float: left;
			width: 100%;
		}

		:deep(.el-input-group--append) { width: 100%; }
		:deep(.el-input__inner) { width: 100%; }

		.list {
			width: calc(100% - 62px);
			float: left;
			margin-bottom: 20px;
			overflow: hidden;
			border: 1px solid #eee;
			border-top: 0px;

			ul {
				width: 96%;
				margin: 10px 2%;
				float: left;
				overflow: hidden;

				li {
					width: 33.3%;
					float: left;
					line-height: 30px;
					cursor: pointer;
					height: 30px;

					span {
						float: left;
						&:nth-child(2) { margin: 0 10px 0 5px; }
					}

					i {
						line-height: 30px;
						color: red;
						margin-left: 5px;
						float: left;
						margin-top: 1px;
						cursor: pointer;
					}
				}
			}
		}

		.dietList {
			ul { li { width: 50%; } }
		}

		:deep(.el-form-item) { width: 100%; float: left; }

		// mallProduct 专用样式
		.product-action {
			display: flex;
			align-items: center;
			.product-count {
				margin-left: 12px;
				color: #679ff6;
				font-size: 13px;
			}
		}
		.product-list {
			margin-top: 12px;
			width: 100%;
			display: flex;
			flex-wrap: wrap;
		}
	}
}

.longInput {
	width: calc(100% - 40px);
	:deep(.el-input__inner) { width: 100% !important; }
}
</style>
